/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react'
import getblogcategoriespost, {
    Blogcategoryposts,
    BlogCategoryTypesForPost
} from '@pylot-data/api/operations/get-blogcategoriespost'
import { useRouter } from 'next/router'
import Sidebar from '@components/blog/Sidebar'
import BottomSection from '@components/blog/BottomSection'
import { useTranslation } from 'react-i18next'
import Link from 'next/link'
import s from '@pagestyles/Blog.module.scss'
import { Layout } from '@components/common'
import { Breadcrumbs } from '@components/alfakher/pylot-breadcrumbs'
import { Button, LoadingDots } from '@corratech/pylot-ui'
import cn from 'classnames'
import {
    authorTitle,
    authorName,
    authorTime,
    generateBlogIdentifier,
    splitBreadcrumb
} from '@components/common/CMSBlockConst/cms-block-util'
import { RichContent } from '@corratech/pylot-rich-content'
import { GetServerSidePropsContext, InferGetStaticPropsType } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { SEO, SEO_ENUMS } from '@components/seo'
import getBlogCategorySeoDetails from '@pylot-data/api/operations/get-blog-categoyr-seo-details'
import SearchPost from '@components/blog/SearchPost'
import { getStaticProps } from '../index'

export const getServerSideProps = async ({
    locale,
    params
}: GetServerSidePropsContext) => {
    const categoryId = params!.categoryId as string
    const blogSeoDetails = await getBlogCategorySeoDetails({
        categoryId: categoryId
    })
    return {
        props: {
            blogSeoDetails: blogSeoDetails?.blogCategory,
            ...(await serverSideTranslations(locale!, ['common', 'blog']))
        }
    }
}

export default function CategoryIdPage({
    blogSeoDetails
}: InferGetStaticPropsType<typeof getStaticProps>): JSX.Element {
    const { t } = useTranslation(['blog'])
    const [numberCount, setNumberCount] = useState<number>(1)
    const [details, setDetails] = useState<BlogCategoryTypesForPost | null>(
        null
    )
    const [searchQueryParam, setSearchQuery] = useState<string>('')
    const router = useRouter()
    const postId: string = (router.query.categoryId || '').toString()
    const postcategoryurl = '/blog/'
    const [loading, setLoading] = useState<boolean>(true)
    const { locale } = router
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            const blogData = await getblogcategoriespost({
                currentPage: numberCount,
                pageSize: 10,
                categoryId: postId,
                locale
            })
            if (!blogData) {
                setLoading(false)
                return {
                    notFound: true
                }
            }
            if (numberCount > 1) {
                setCategoryPosts(blogData.blogPosts)
                return
            }

            setDetails(blogData?.blogPosts)
        }
        resetNumberCount()
        fetchData()
        setLoading(false)
    }, [numberCount, postId])

    const resetNumberCount = () => {
        if (searchQueryParam !== postId) {
            setNumberCount(1)
        }
        setSearchQuery(postId)
    }

    const setCategoryPosts = (blogData: BlogCategoryTypesForPost) => {
        if (blogData?.items.length) {
            if (details && details.items.length) {
                blogData.items = [...details.items, ...blogData.items]
            } else {
                blogData.items = []
            }
        }
        setDetails(blogData)
    }

    const moreDetailsClick = () => {
        setNumberCount(numberCount + 1)
    }

    const renderBlogdataPost = () => {
        if (details?.items.length == 0) {
            return (
                <h2 className={s['errorPostTitle']}>{t('No Result Found')}</h2>
            )
        }
        return (
            <>
                {details?.items.map((post: Blogcategoryposts) => {
                    return (
                        <div className={s['postList']} key={post.post_id}>
                            <div className={s['postImage']}>
                                <Link
                                    href={`${postcategoryurl}${post.identifier}`}
                                >
                                    <img
                                        src={post.featured_list_image}
                                        alt={post.title}
                                        title={post.title}
                                    />
                                </Link>
                            </div>
                            <div className={s['postTitle']}>
                                <Link
                                    href={`${postcategoryurl}${post.identifier}`}
                                >
                                    <a>{post.title}</a>
                                </Link>
                            </div>
                            <h5>
                                {post.author && (
                                    <>
                                        {t(`By`)}{' '}
                                        <Link
                                            href={`${postcategoryurl}author/${generateBlogIdentifier(
                                                post.author?.name
                                            )}/${post?.author?.author_id}`}
                                        >
                                            {authorName(post)}
                                        </Link>
                                    </>
                                )}
                                {authorTime(post)}
                                {authorTitle(post)}
                            </h5>
                            <div className={s['postDescriptionWrapper']}>
                                <span className={s['postDescription']}>
                                    <RichContent
                                        html={post.short_filtered_content}
                                    />
                                </span>
                            </div>
                            <div className={s['readmoreUrl']}>
                                <Link
                                    href={`${postcategoryurl}${post.identifier}`}
                                >
                                    {t('Read More...')}
                                </Link>
                            </div>
                        </div>
                    )
                })}
                {numberCount != details?.total_pages && (
                    <div className={s['fullLoadmore']}>
                        <Button
                            variant="secondary"
                            className={cn(s['load-more'])}
                            onClick={() => moreDetailsClick()}
                        >
                            {t('LOAD MORE')}
                        </Button>
                    </div>
                )}
            </>
        )
    }

    return (
        <div className={s['blog-container']}>
            {blogSeoDetails && (
                <SEO
                    blogCategory={blogSeoDetails}
                    page={SEO_ENUMS.BCP}
                    blogBreadCrumbs={{ name: [postId] }}
                />
            )}
            <div className={s['container-classes']}>
                <div className={s['breadcrumb-inner']}>
                    <div className={s['breadcrumb-wrapper']}>
                        <Breadcrumbs
                            product={{
                                categories: [
                                    {
                                        name: t('Blog'),
                                        url_path: 'blog'
                                    }
                                ],
                                name: splitBreadcrumb(postId)
                            }}
                            separator=" | "
                            urlPrefix={t('')}
                        />
                    </div>
                </div>
                <div className={s['toggleSearchBar']}>
                    <SearchPost />
                </div>
                <div className={s['fullContent']}>
                    <div className={s['leftSection']}>
                        <h1 className={s['resultHeading']}>
                            <span>{splitBreadcrumb(postId)}</span>
                        </h1>
                        {loading ? (
                            <span className={s['postLoader']}>
                                <LoadingDots />
                            </span>
                        ) : (
                            <div className={s['mainPost']}>
                                {renderBlogdataPost()}
                            </div>
                        )}
                    </div>
                    <Sidebar />
                </div>
            </div>
            <BottomSection />
        </div>
    )
}

CategoryIdPage.Layout = Layout
